/**
 * Draw a rounded tooltip with a downward pointer, similar to the provided screenshot.
 * The tooltip is positioned ABOVE the (x, y) anchor, where (x, y) is the pointer tip.
 */

export interface TooltipOptions {
  /** Maximum tooltip width in CSS pixels (text will wrap). */
  maxWidth?: number
  /** Canvas CSS font string. */
  font?: string
  /** Horizontal padding inside the bubble. */
  paddingX?: number
  /** Vertical padding inside the bubble. */
  paddingY?: number
  /** Corner radius for the bubble. */
  radius?: number
  /** Pointer base width (in CSS px). */
  pointerW?: number
  /** Pointer height (in CSS px). */
  pointerH?: number
  /** Background color for the bubble. */
  bg?: string
  /** Text color. */
  textColor?: string
  /** Shadow blur radius. */
  shadowBlur?: number
  /**
   * Optional viewport width in CSS pixels used to keep the tooltip on screen.
   * If omitted, the function will try to infer it from the canvas element.
   */
  viewportWidth?: number
}

export interface TooltipMetrics {
  /** Top-left x of the bubble (excluding the pointer). */
  x: number
  /** Top-left y of the bubble (excluding the pointer). */
  y: number
  /** Bubble width. */
  width: number
  /** Bubble height including the pointer. */
  height: number
  /** Lines actually rendered after wrapping. */
  lines: string[]
}

/** Utility: constrain a value to [min, max]. */
const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v))

/** Create a rounded-rect path. */
function roundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.max(0, Math.min(r, Math.min(w / 2, h / 2)))
  ctx.moveTo(x + rr, y)
  ctx.arcTo(x + w, y, x + w, y + h, rr)
  ctx.arcTo(x + w, y + h, x, y + h, rr)
  ctx.arcTo(x, y + h, x, y, rr)
  ctx.arcTo(x, y, x + w, y, rr)
  ctx.closePath()
}

/** Soft wrap a string to lines that fit maxWidth using current ctx.font. */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = (text || '').split(/\s+/g)
  const lines: string[] = []
  let line = ''
  for (const w of words) {
    const test = line ? line + ' ' + w : w
    if (ctx.measureText(test).width <= maxWidth) {
      line = test
    } else {
      if (line) lines.push(line)
      if (ctx.measureText(w).width > maxWidth) {
        lines.push(...splitLongWord(ctx, w, maxWidth))
        line = ''
      } else {
        line = w
      }
    }
  }
  if (line) lines.push(line)
  return lines.length ? lines : ['']
}

/** Break a single overlong word to fit within maxWidth. */
function splitLongWord(ctx: CanvasRenderingContext2D, word: string, maxWidth: number): string[] {
  const chars = [...word]
  let chunk = ''
  const parts: string[] = []
  for (const ch of chars) {
    const test = chunk + ch
    if (ctx.measureText(test).width <= maxWidth) {
      chunk = test
    } else {
      if (chunk) parts.push(chunk)
      chunk = ch
    }
  }
  if (chunk) parts.push(chunk)
  return parts
}

/**
 * Draw a rounded tooltip with a downward pointer.
 * The tooltip bubble is kept within the horizontal viewport when possible.
 *
 * @param ctx 2D canvas context
 * @param text Tooltip text
 * @param x Anchor X (CSS px) where the pointer tip should be placed
 * @param y Anchor Y (CSS px) where the pointer tip should be placed
 * @param opts Rendering options
 * @returns Geometry/lines that were drawn
 */
export function drawTooltip(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  opts: TooltipOptions = {}
): TooltipMetrics {
  const {
    maxWidth = 340,
    font = '14px Inter',
    paddingX = 14,
    paddingY = 10,
    radius = 10,
    pointerW = 12,
    pointerH = 10,
    bg = 'rgba(0,0,0,0.92)',
    textColor = '#ffffff',
    shadowBlur = 14,
    viewportWidth,
  } = opts

  ctx.save()
  ctx.font = font
  ctx.textBaseline = 'middle'

  const innerMax = Math.max(20, maxWidth - paddingX * 2)
  const lines = wrapText(ctx, text, innerMax)
  const lineWidths = lines.map((l) => ctx.measureText(l).width)
  const textW = Math.min(innerMax, Math.max(0, ...lineWidths))
  const lineH = Math.ceil(parseInt(font, 10) * 1.25) || 20 // rough line-height
  const textH = lines.length * lineH
  const bw = Math.ceil(textW + paddingX * 2)
  const bh = Math.ceil(textH + paddingY * 2)

  // Position the bubble above the pointer.
  let left = Math.round(x - bw / 2)
  const top = Math.round(y - bh - pointerH)

  // Keep horizontally on-screen using provided viewportWidth or infer from canvas.
  const canvasEl = ctx.canvas as HTMLCanvasElement
  const inferredVW = (() => {
    try {
      const rect = canvasEl.getBoundingClientRect()
      return rect.width || canvasEl.width
    } catch {
      return canvasEl.width
    }
  })()
  const vw = (typeof viewportWidth === 'number' ? viewportWidth : inferredVW) - 16 // 8px margin each side
  if (Number.isFinite(vw)) {
    if (left < 8) left = 8
    if (left + bw > vw) left = vw - bw
  }

  // Bubble
  ctx.beginPath()
  roundedRectPath(ctx, left, top, bw, bh, radius)
  ctx.shadowColor = 'rgba(0,0,0,0.45)'
  ctx.shadowBlur = shadowBlur
  ctx.fillStyle = bg
  ctx.fill()

  // Pointer (downward triangle)
  const baseY = top + bh
  const halfW = pointerW / 2
  const baseLeft = clamp(x - halfW, left + radius, left + bw - radius - pointerW)
  ctx.beginPath()
  ctx.moveTo(x, y) // tip
  ctx.lineTo(baseLeft, baseY)
  ctx.lineTo(baseLeft + pointerW, baseY)
  ctx.closePath()
  ctx.fill()

  // Text
  ctx.shadowBlur = 0
  ctx.fillStyle = textColor
  let ty = top + paddingY + lineH / 2
  for (const line of lines) {
    ctx.fillText(line, left + paddingX, ty)
    ty += lineH
  }

  ctx.restore()

  return { x: left, y: top, width: bw, height: bh + pointerH, lines }
}

// --- Example usage ---
// const canvas = document.querySelector('canvas')!;
// const ctx = canvas.getContext('2d')!;
// drawTooltip(ctx, '58: Разрыв цепи ВБР', 160, 120);

const canvasTooltip = {
  drawTooltip,
}
export default canvasTooltip
